'use es6';

import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import styled from 'foundations-theming/utils';
import { ItemTypes } from '../../utils/ItemTypes';
import { useEffect } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
const DraggableListItemWrapper = styled.div.withConfig({
  displayName: "UIDragListItem__DraggableListItemWrapper"
})(({
  isDragging
}) => ({
  opacity: isDragging ? 0 : 1
}));
const UIDragListItem = props => {
  const {
    accept = ItemTypes.ITEM,
    callOnOrderChangeOnDragEnd,
    direction,
    disableDropOnHover,
    dragId: __dragId,
    draggable = true,
    droppable = true,
    id,
    index,
    moveItem,
    onDragChange,
    children,
    onHover
  } = props;
  const ref = useRef(null);
  const [{
    handlerId,
    highlighted,
    hovered
  }, connectDropTarget] = useDrop({
    accept,
    collect: monitor => {
      return {
        handlerId: monitor.getHandlerId(),
        highlighted: monitor.canDrop(),
        hovered: monitor.isOver()
      };
    },
    canDrop: () => {
      return droppable;
    },
    hover: (item, monitor) => {
      if (disableDropOnHover) {
        return;
      }
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = direction === 'vertical' ? (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2 : (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = direction === 'vertical' ? clientOffset.y - hoverBoundingRect.top : clientOffset.x - hoverBoundingRect.left;

      /**
       * Only perform the move when the mouse has crossed half of the items
       * height or, when horizontal, width.
       */
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      onHover(dragIndex, hoverIndex);

      /**
       * Mutate the monitor. Generally it's better to avoid mutations,
       * but it's good here for the sake of performance to avoid expensive
       * index searches.
       */
      item.index = hoverIndex;
    },
    drop: item => {
      /**
       * moveItem is called on drop in order to make the drop functionality work with keyboard controls.
       */
      if (!callOnOrderChangeOnDragEnd) {
        moveItem(item.index, index);
      }
    }
  });
  const [{
    ariaLabel,
    isDragging
  }, connectDragSource] = useDrag({
    type: accept,
    item: () => {
      return {
        ariaLabel,
        id,
        index,
        children
      };
    },
    canDrag: () => {
      return draggable;
    },
    collect: monitor => {
      return {
        isDragging: monitor.isDragging()
      };
    },
    end: item => {
      if (callOnOrderChangeOnDragEnd) {
        moveItem(item.index, index);
      }
    }
  });

  // eslint-disable-next-line react-hooks/refs
  connectDragSource(connectDropTarget(ref));
  useEffect(() => {
    if (onDragChange) onDragChange(isDragging);
  }, [isDragging, onDragChange]);
  return /*#__PURE__*/_jsx(DraggableListItemWrapper, {
    "aria-label": ariaLabel,
    "aria-pressed": isDragging,
    "data-test-id": "uidraglistitem",
    "data-handler-id": handlerId,
    highlighted: highlighted,
    hovered: hovered,
    isDragging: isDragging,
    ref: ref,
    role: "button",
    tabIndex: 0,
    children: children
  });
};
export default UIDragListItem;

/*
 * Prop types (preserved for future TypeScript migration):
 *
 * UIDragListItem.propTypes = {
 *   accept: PropTypes.string,
 *   callOnOrderChangeOnDragEnd: PropTypes.bool,
 *   children: PropTypes.node.isRequired,
 *   direction: PropTypes.oneOf(['horizontal', 'vertical']),
 *   disableDropOnHover: PropTypes.bool,
 *   dragId: PropTypes.string, (deprecated)
 *   draggable: PropTypes.bool,
 *   droppable: PropTypes.bool,
 *   id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
 *   index: PropTypes.number,
 *   moveItem: PropTypes.func,
 *   onDragChange: PropTypes.func,
 *   onHover: PropTypes.func,
 * };
 */